import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button, Spinner } from '../design-system';
import NoteEditor from './NoteEditor';

const NoteEditorModal = ({
  isOpen,
  onClose,
  onSave,
  note = null, // null for create, note object for edit
  tagSuggestions = [],
  saving = false
}) => {
  const [heading, setHeading] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState(null);
  const [tags, setTags] = useState([]);
  const [errors, setErrors] = useState({});

  // Initialize form when modal opens or note changes
  useEffect(() => {
    if (isOpen) {
      if (note) {
        // Edit mode - populate with existing note data
        setHeading(note.heading || '');
        setSummary(note.summary || '');
        // Parse content if it's a string
        try {
          const parsedContent = typeof note.content === 'string' 
            ? JSON.parse(note.content) 
            : note.content;
          setContent(parsedContent);
        } catch (e) {
          setContent({ type: 'doc', content: [] });
        }
        setTags(note.tags || []);
      } else {
        // Create mode - reset form
        setHeading('');
        setSummary('');
        setContent({ type: 'doc', content: [] });
        setTags([]);
      }
      setErrors({});
    }
  }, [isOpen, note]);

  const handleSave = () => {
    // Validate
    const newErrors = {};
    if (!heading.trim()) {
      newErrors.heading = 'Heading is required';
    }
    if (!summary.trim()) {
      newErrors.summary = 'Summary is required';
    }
    if (!content) {
      newErrors.content = 'Content is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Call save handler
    onSave({
      heading: heading.trim(),
      summary: summary.trim(),
      content: content,
      tags: tags
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full h-full max-w-5xl max-h-[95vh] md:max-h-[90vh] m-4 flex flex-col bg-white rounded-lg shadow-xl overflow-hidden relative">
        {/* Close button - Floating in top right */}
        <button
          onClick={onClose}
          disabled={saving}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white rounded-full shadow-sm hover:bg-gray-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Editor Content - Takes up most of the space */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <NoteEditor
            heading={heading}
            summary={summary}
            content={content}
            tags={tags}
            onHeadingChange={setHeading}
            onSummaryChange={setSummary}
            onContentChange={setContent}
            onTagsChange={setTags}
            tagSuggestions={tagSuggestions}
            disabled={saving}
          />
          {errors.heading && (
            <div className="px-6 py-2">
              <p className="text-sm text-red-600">{errors.heading}</p>
            </div>
          )}
          {errors.summary && (
            <div className="px-6 py-2">
              <p className="text-sm text-red-600">{errors.summary}</p>
            </div>
          )}
          {errors.content && (
            <div className="px-6 py-2">
              <p className="text-sm text-red-600">{errors.content}</p>
            </div>
          )}
        </div>

        {/* Footer - Save/Cancel buttons */}
        <div className="flex flex-row justify-end gap-3 p-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            size="medium"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            variant="primary"
            size="medium"
          >
            {saving ? (
              <>
                <Spinner size="small" className="mr-2" />
                Saving...
              </>
            ) : (
              'Save Note'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NoteEditorModal;
